import type { Matter, MatterContactConnection } from "@/lib/types";

export const getPrimaryItem = <T extends Record<string, unknown>>(
  items: T[],
  primaryKey: keyof T,
): T | undefined => {
  return items.find((item) => Boolean(item[primaryKey])) ?? items[0];
};

export function resolveContactPhonesForMatter(
  _matter: Matter,
  connection: MatterContactConnection,
): NonNullable<MatterContactConnection["contact"]>["phones"] {
  return connection.contact.phones;
}

export function resolveContactEmailsForMatter(
  _matter: Matter,
  connection: MatterContactConnection,
): NonNullable<MatterContactConnection["contact"]>["emails"] {
  return connection.contact.emails;
}

export const getPrimaryPhone = (connection?: MatterContactConnection) => {
  return getPrimaryItem(connection?.contact.phones ?? [], "isPrimary");
};

export const getPrimaryEmail = (connection?: MatterContactConnection) => {
  return getPrimaryItem(connection?.contact.emails ?? [], "isPrimary");
};

export const getResolvedPrimaryPhone = (
  matter: Matter,
  connection?: MatterContactConnection,
) => {
  if (!connection) return undefined;
  const phones = resolveContactPhonesForMatter(matter, connection);
  return getPrimaryItem(phones, "isPrimary");
};

export const getResolvedPrimaryEmail = (
  matter: Matter,
  connection?: MatterContactConnection,
) => {
  if (!connection) return undefined;
  const emails = resolveContactEmailsForMatter(matter, connection);
  return getPrimaryItem(emails, "isPrimary");
};

export const getPrimaryAddress = (connection?: MatterContactConnection) => {
  return getPrimaryItem(connection?.contact.addresses ?? [], "isPrimary");
};

/** Title, description, and button labels for removing a matter–contact connection. */
export function getRemoveMatterContactConnectionModalCopy(
  connectionRoleLabel: string,
) {
  const role = connectionRoleLabel.trim();
  if (!role) {
    return {
      title: "Remove this contact from the matter?",
      description:
        "This will remove this contact from this matter. This action cannot be undone.",
      buttonText: "Remove contact",
      deleteButtonText: "Remove contact",
    };
  }

  return {
    title: `Remove ${role}?`,
    description: `This will remove the ${role} from this matter. This action cannot be undone.`,
    buttonText: `Remove ${role}`,
    deleteButtonText: `Remove ${role}`,
  };
}
